import React, { useMemo, useRef, useState } from 'react';
import { Formik, FieldArray } from 'formik';
import { PropTypes } from 'prop-types';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Col, Row } from 'antd';
import { Form } from 'formik-antd';
import { useDispatch } from 'react-redux';
import { PoweroffOutlined } from '@ant-design/icons';
import { Box, FormInput, CustomInput, Button } from 'atoms';
import { Debug } from '../Debug';
import { getPincodeList } from 'store/pageSlice';
import { updateClient } from 'store/clientSlice';
import { ACL, EditModeBtn } from 'components/common';
// import { useQuery } from 'hooks';
import { pinError } from 'utils';
import { ActionAddButton, CancelButton } from 'utils/commonStyled';

const BoxSpace = styled(Box)`
	padding: 16px;
	margin-top: 12px;
`;
const FormSchema = Yup.object({
	billing_info: Yup.array().of(
		Yup.object().shape({
			address: Yup.string()
				.required()
				.min(6)
				.max(200)
				.label('Address')
				.matches(/^[a-zA-Z0-9# ,-]*$/, 'Please enter valid address'),
			zone: Yup.string()
				.required()
				.min(4)
				.max(30)
				.label('Zone')
				.matches(/^[a-zA-Z ]*$/, 'Please enter valid zone'),
			state: Yup.string()
				.required()
				.min(3)
				.max(30)
				.label('State')
				.matches(/^[a-zA-Z ]*$/, 'Please enter valid state'),
			city: Yup.string()
				.required()
				.min(3)
				.max(30)
				.label('City')
				.matches(/^[a-zA-Z ]*$/, 'Please enter valid city'),
			pin_code: Yup.string()
				.required()
				.label('Pin Code')
				.min(6, pinError)
				.max(6, pinError)
				.matches(/^[0-9]*$/, 'Please enter valid pin code'),
		})
	),
});

function getNewBillingRowData() {
	return {
		id: Math.random(),
		address: undefined,
		pin_code: undefined,
		zone: undefined,
		state: undefined,
		city: undefined,
	};
}

function addRemovePayment(arrayHelpers, index) {
	if (index === 0) {
		// arrayHelpers.insert(index + 1, getNewBillingRowData());
		arrayHelpers.push(getNewBillingRowData());
	} else {
		arrayHelpers.remove(index);
	}
}

function getBillingPreFilledData(formData) {
	if (formData?.length === 0) {
		/**
		 * On first time if not data available, add one row data
		 */
		return [getNewBillingRowData()];
	}
	/**
	 * If data already available create rows based on that
	 */
	return formData?.map((item) => ({
		id: Math.random(),
		...item,
	}));
}

function setLocationData(refObject, index, city = undefined, state = undefined, zone = undefined) {
	refObject?.setFieldValue(`billing_info[${index}].city`, city);
	refObject?.setFieldValue(`billing_info[${index}].state`, state);
	refObject?.setFieldValue(`billing_info[${index}].zone`, zone);
}

export default function BillingInfoForm({ isEditMode = false, formData = [], otherFormsData }) {
	const dispatch = useDispatch();
	const ref = useRef(null);
	const navigate = useNavigate();
	// const [query] = useQuery();
	const [editMode, setEditMode] = useState(false);

	const initialValues = useMemo(
		() => ({
			billing_info: getBillingPreFilledData(formData),
		}),
		[formData]
	);

	const handleSubmit = (values, { setErrors, setSubmitting }) => {
		const payload = {
			...otherFormsData,
			billing_info: values?.billing_info,
		};
		if (isEditMode) {
			payload.update_type = 'client';
		} else {
			payload.create_type = 'client';
		}

		console.log(setErrors);

		dispatch(updateClient({ req_data: payload, info: 'billing_info', setErrors, isEditMode }))
			.then((res) => {
				console.log(res);
				window.location = `${window.origin}/clients/${res.data.id}?tab=4`;
				setSubmitting(false);

				// navigate(`/clients/${res.data.id}?tab=4`);
			})
			.catch(() => {
				setSubmitting(false);
			});
	};

	function handlePicodeChange(e, index) {
		const pin_code = e?.target?.value;

		if (pin_code?.length === 6) {
			dispatch(getPincodeList(pin_code))
				.then((res) => {
					const { city, state, zone } = res?.rows?.[0] || {};
					if (ref?.current) {
						setLocationData(ref.current, index, city, state, zone);
					}
				})
				.catch((er) => {
					console.log('pincode error', er);
				});
		} else {
			console.log('not a valid pincode');
			if (ref?.current) {
				setLocationData(ref.current, index);
			}
		}
	}

	function handleEdit() {
		setEditMode(!editMode);
	}

	return (
		<Formik
			innerRef={ref}
			initialValues={initialValues}
			validationSchema={FormSchema}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			{({ values }) => (
				<Box>
					<ACL action="update" module="client">
						{isEditMode && <EditModeBtn handleEdit={handleEdit} />}
					</ACL>
					<Form className={editMode === false ? 'disabled-div' : ''}>
						<FieldArray
							name="billing_info"
							render={(arrayHelpers) => (
								<div>
									{values?.billing_info?.map((rowData, index) => (
										<BoxSpace key={rowData?.id}>
											<Row gutter={16} justify="end">
												{index > 0 && (
													<CancelButton
														onClick={() =>
															addRemovePayment(arrayHelpers, index)
														}
														type="button"
														icon={<PoweroffOutlined />}
													>
														{index === 0
															? 'Add more Billing Address'
															: 'Remove'}
													</CancelButton>
												)}
											</Row>
											<Row gutter={16} justify="space-between">
												<Col lg={12}>
													<FormInput
														name={`billing_info[${index}].address`}
														label="Address *"
													>
														<CustomInput
															name={`billing_info[${index}].address`}
														/>
													</FormInput>
												</Col>
												<Col lg={12}>
													<FormInput
														name={`billing_info[${index}].pin_code`}
														label="Pin Code *"
														onChange={(e) =>
															handlePicodeChange(e, index)
														}
													>
														<CustomInput
															name={`billing_info[${index}].pin_code`}
															maxLength={6}
														/>
													</FormInput>
												</Col>
											</Row>
											<Row gutter={16} justify="space-between">
												<Col lg={12}>
													<FormInput
														name={`billing_info[${index}].zone`}
														label="Zone *"
													>
														<CustomInput
															name={`billing_info[${index}].zone`}
															disabled
														/>
													</FormInput>
												</Col>
												<Col lg={12}>
													<FormInput
														name={`billing_info[${index}].state`}
														label="State *"
													>
														<CustomInput
															name={`billing_info[${index}].state`}
															disabled
														/>
													</FormInput>
												</Col>
											</Row>
											<Row gutter={16} justify="space-between">
												<Col lg={12}>
													<FormInput
														name={`billing_info[${index}].city`}
														label="City *"
													>
														<CustomInput
															name={`billing_info[${index}].city`}
															disabled
														/>
													</FormInput>
												</Col>
												<Col lg={12}></Col>
											</Row>
											<Row gutter={16} justify="space-between">
												<Col span={4}>
													{index === 0 && (
														<ActionAddButton
															type="primary"
															onClick={() =>
																addRemovePayment(
																	arrayHelpers,
																	index
																)
															}
															icon={<PoweroffOutlined />}
														>
															{index === 0
																? '+ Add more Billing Address'
																: 'Remove'}
														</ActionAddButton>
													)}
												</Col>
											</Row>
										</BoxSpace>
									))}
								</div>
							)}
						/>
						<Box backgroundColor="#f5f5f5" textAlign="right" padding="1.5rem">
							<CancelButton onClick={() => navigate('/client-list')} type="button">
								Cancel
							</CancelButton>
							<Button borderRadius="4px" backgroundColor="#18405d" type="submit">
								Update
							</Button>
						</Box>
						<Debug />
					</Form>
				</Box>
			)}
		</Formik>
	);
}

BillingInfoForm.propTypes = {
	formData: PropTypes.any,
	isEditMode: PropTypes.bool,
	otherFormsData: PropTypes.object,
};
